import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import LanguageToggle from "@/components/LanguageToggle";
import { UserPlus, ArrowLeft, ShieldCheck } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return setErr(t("reg.password_too_short"));
    setLoading(true);
    setErr("");
    try {
      await register(form);
      navigate("/my-cases", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="signup-page">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white" style={{ background: "var(--brand)" }}>
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> {t("login.back")}
        </Link>
        <div>
          <div className="font-display text-4xl font-black tracking-tight">{t("reg.title_panel")}</div>
          <p className="mt-3 max-w-sm text-white/70">
            {t("reg.tagline_panel")}
          </p>
          <div className="mt-8 space-y-3 text-sm text-white/80">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#E85D04]" /> {t("reg.bullet.encrypted")}</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#E85D04]" /> {t("reg.bullet.nofee")}</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#E85D04]" /> {t("reg.bullet.licensed")}</div>
          </div>
        </div>
        <div className="text-xs text-white/40">{t("reg.never_sold")}</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="card-soft w-full max-w-md p-8" data-testid="signup-form">
          <div className="flex items-start justify-between gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
              <UserPlus className="h-4 w-4 text-[#11322A]" />
            </div>
            <LanguageToggle variant="compact" />
          </div>
          <h1 className="mt-6 font-display text-2xl font-black tracking-tight text-[#11322A]">{t("reg.title")}</h1>
          <p className="mt-1 text-sm text-[#5C6B68]">{t("reg.subtitle")}</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="label-cap">{t("reg.name")}</label>
              <input className="input-field mt-2" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="signup-name" />
            </div>
            <div>
              <label className="label-cap">{t("reg.email")}</label>
              <input type="email" className="input-field mt-2" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="signup-email" />
            </div>
            <div>
              <label className="label-cap">{t("reg.phone")}</label>
              <input type="tel" className="input-field mt-2" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="signup-phone" />
            </div>
            <div>
              <label className="label-cap">{t("reg.password")}</label>
              <input type="password" className="input-field mt-2" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} data-testid="signup-password" />
            </div>
            {err && <div className="rounded-lg bg-[#FDE8E6] border border-[#F5B6B0] px-3 py-2 text-sm text-[#B91C1C]" data-testid="signup-error">{err}</div>}
            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="signup-submit">
              {loading ? t("reg.submitting") : t("reg.submit")}
            </button>
          </div>
          <p className="mt-6 text-sm text-[#5C6B68]">
            {t("reg.have_account")}{" "}
            <Link to="/login" className="font-bold text-[#11322A] hover:text-[#E85D04]" data-testid="signup-to-login">{t("reg.sign_in")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
